import type { SitemapEntry } from "./types";

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function isValidLastmod(value: string | undefined): value is string {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getTime() <= Date.now() + 86_400_000;
}

export function toSitemapDate(value: string | undefined): string | undefined {
  if (!isValidLastmod(value)) {
    return undefined;
  }
  return new Date(value).toISOString();
}

export function buildSitemapXml(entries: SitemapEntry[]): string {
  const unique = dedupeEntries(entries).slice(0, 50_000);
  const urls = unique
    .map((entry) => {
      const lastmod = toSitemapDate(entry.lastmod);
      const lastmodXml = lastmod ? `\n    <lastmod>${escapeXml(lastmod)}</lastmod>` : "";
      return `  <url>\n    <loc>${escapeXml(entry.loc)}</loc>${lastmodXml}\n  </url>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}

export function buildCsv(entries: SitemapEntry[]): string {
  const rows = dedupeEntries(entries).map((entry) => [entry.loc, toSitemapDate(entry.lastmod) ?? ""]);
  return [["loc", "lastmod"], ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n");
}

export function buildIndexNowPayload(host: string, urls: string[], key = "", keyLocation = ""): string {
  const payload: { host: string; key: string; urlList: string[]; keyLocation?: string } = {
    host,
    key,
    urlList: [...new Set(urls)].slice(0, 10_000)
  };
  if (keyLocation.trim()) {
    payload.keyLocation = keyLocation.trim();
  }
  return JSON.stringify(payload, null, 2);
}

function dedupeEntries(entries: SitemapEntry[]): SitemapEntry[] {
  const seen = new Map<string, SitemapEntry>();
  for (const entry of entries) {
    if (!seen.has(entry.loc)) {
      seen.set(entry.loc, entry);
    }
  }
  return [...seen.values()];
}
